import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaComments, FaCalendarCheck, FaTelegramPlane } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { trackForm, trackError, trackLeadConversion } from '../utils/gtm';
import AppCtaLink from './AppCtaLink';
import FormThankYouPopup from './FormThankYouPopup';
import { postJsonWithRetry } from '../utils/postJsonWithRetry';
import { Link, useParams } from 'react-router-dom';
import { COOKIES_VERSION } from '../data/legalEntity';
import { consentModeLabel } from '../utils/consent';

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const { language: routeLanguage } = useParams();
  const language = routeLanguage || (i18n.language || 'en').split('-')[0];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
    preferredContact: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' або 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    
    console.log('Спроба відправки форми контактів:', formData);
    
    // Відстеження початку відправки форми
    trackForm('contact_form', 'start');
    
    try {
      // Підготовка даних форми
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        message: formData.message || '',
        preferredContact: formData.preferredContact,
        consent: formData.consent,
        consentVersion: COOKIES_VERSION,
        idempotencyKey: `contact-${formData.email}-${Date.now()}`,
        formType: 'contact'
      };
      
      console.log('Відправляємо дані на сервер:', JSON.stringify(payload));
      
      // Відправка даних на сервер (1 retry при transient 5xx / Mongo blip)
      const { response, data } = await postJsonWithRetry('/api/contacts', {
        body: payload,
        headers: {
          'Accept-Language': (i18n.language || 'en').split('-')[0]
        },
        retries: 1
      });
      
      console.log('Отримано відповідь від сервера:', response.status, response.statusText);
      console.log('Дані відповіді:', data);
      
      if (response.ok) {
        if (data.emailSent) {
          // Повний успіх - дані збережені і повідомлення відправлено
          console.log('Форму успішно відправлено, емейл надіслано');
          setSubmitStatus('success');
          setShowThankYou(true);
          
          // Відстеження успішної відправки форми
          trackForm('contact_form', 'complete', 'email_sent');
          trackLeadConversion({
            formName: 'contact_form',
            pageType: 'contact',
            placement: 'home_contact',
            language,
            leadId: data.leadId || data.data?._id,
            consentState: consentModeLabel()
          });
        } else {
          // Частковий успіх - дані збережені, але пошта не відправлена
          console.log('Форму збережено, але емейл не надіслано:', data.emailErrorDetails || 'причина не вказана');
          setSubmitStatus('partial');
          setErrorMessage(data.message || t('contact_form.email_error'));
          setShowThankYou(true);
          
          // Відстеження часткового успіху
          trackForm('contact_form', 'complete', 'partial_email_error');
          trackLeadConversion({
            formName: 'contact_form',
            pageType: 'contact',
            placement: 'home_contact',
            language,
            leadId: data.leadId || data.data?._id,
            consentState: consentModeLabel()
          });
        }
        
        // Очищення полів форми
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          consent: false,
          preferredContact: 'email'
        });
      } else {
        // Помилка обробки запиту
        console.error('Помилка відправки форми:', data);
        setSubmitStatus('error');
        setErrorMessage(data.message || t('contact_form.server_error'));
        
        // Відстеження помилки серверу
        trackForm('contact_form', 'error', 'server_error');
        trackError(`Contact form server error: ${data.message || 'Unknown server error'}`, response.status.toString());
      }
    } catch (error) {
      // Помилка мережі чи інша непередбачена помилка
      console.error('Критична помилка відправки форми:', error);
      console.error('Тип помилки:', error.name);
      console.error('Повідомлення помилки:', error.message);
      console.error('Стек помилки:', error.stack);
      
      setSubmitStatus('error');
      setErrorMessage(t('contact_form.network_error'));
      
      // Відстеження мережевої помилки
      trackForm('contact_form', 'error', 'network_error');
      trackError(`Contact form network error: ${error.message}`, error.name);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Повідомлення про успішну відправку
  const renderSuccessMessage = () => (
    <div className="form-success-message">
      <h3>{t('contact_form.success_title')}</h3>
      <p>{t('contact_form.success_message')}</p>
    </div>
  );

  // Повідомлення про частковий успіх (дані збережені, але пошта не відправлена)
  const renderPartialSuccessMessage = () => (
    <div className="form-partial-success-message">
      <h3>{t('contact_form.partial_success_title', 'Request received')}</h3>
      <p>{errorMessage || t('contact_form.partial_success_message', 'Your request has been saved, but there was a technical issue sending messages. Our team is looking into it and will contact you soon.')}</p>
    </div>
  );

  // Повідомлення про помилку
  const renderErrorMessage = () => (
    <div className="form-error-message">
      <h3>{t('contact_form.error_title')}</h3>
      <p>{errorMessage || t('contact_form.error_message')}</p>
    </div>
  );

  // Основна форма
  const renderForm = () => (
    <motion.form 
      className="contact-form"
      method="post"
      action="/api/contacts"
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit}
    >
      <div className="form-group">
        <label htmlFor="name">{t('contact_form.name_label')}</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('contact_form.name_placeholder')}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">{t('contact_form.email_label')}</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={t('contact_form.email_placeholder')}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">{t('contact_form.phone_label')}</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={t('contact_form.phone_placeholder')}
          disabled={isSubmitting}
          required={false}
        />
      </div>

      <div className="form-group">
        <label htmlFor="message">{t('contact_form.message_label')}</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder={t('contact_form.message_placeholder')}
          disabled={isSubmitting}
          required={false}
        />
      </div>

      <div className="checkbox-group">
        <input
          type="checkbox"
          id="consent"
          name="consent"
          checked={formData.consent}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />
        <label htmlFor="consent">
          {t('contact_form.consent_text')}{' '}
          <Link to={`/${language}/privacy`}>{t('footer.privacy')}</Link>
          {', '}
          <Link to={`/${language}/terms`}>{t('footer.terms')}</Link>
          {' '}{t('forms.and', 'and')}{' '}
          <Link to={`/${language}/cookies`}>{t('footer.cookies', 'Cookies')}</Link>.
        </label>
      </div>

      <div className="form-group">
        <label htmlFor="preferredContact">{t('contact_form.preferred_contact_label')}</label>
        <select
          id="preferredContact"
          name="preferredContact"
          value={formData.preferredContact}
          onChange={handleChange}
          disabled={isSubmitting}
        >
          <option value="email">{t('contact_form.email_option')}</option>
          <option value="phone">{t('contact_form.phone_option')}</option>
          <option value="telegram">{t('contact_form.telegram_option')}</option>
        </select>
      </div>

      <motion.button 
        type="submit" 
        className="submit-button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        disabled={isSubmitting}
      >
        {isSubmitting ? t('contact_form.submitting') : t('contact_form.submit_button')}
      </motion.button>
    </motion.form>
  );

  return (
    <section className="contact-section" id="contact-us">
      <div className="container">
        <header className="contact-header">
          <h2 className="section-title">{t('contact_form.title')}</h2>
          <p className="section-subtitle">{t('contact_form.subtitle')}</p>
        </header>

        <div className="contact-container">
          <div className="contact-info">
            <motion.div
              className="info-card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <div className="info-title">
                <FaComments />
                {t('contact_form.app_button')}
              </div>
              <div className="info-content">
                {t('contact_form.app_description')}
                <br /><br />
                <AppCtaLink platform="web" placement="home_contact_sidebar">
                  chat.psyhelp.info
                </AppCtaLink>
              </div>
            </motion.div>

            <motion.div
              className="info-card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.45, delay: 0.08, ease: 'easeOut' }}
            >
              <div className="info-title">
                <FaCalendarCheck />
                {t('contact_form.free_session_title')}
              </div>
              <div className="info-content">
                {t('contact_form.free_session_description')}
                <br /><br />
                <a href="https://psyhelp.info/zayavka-po-dopomogu/" target="_blank" rel="noopener noreferrer">
                  {t('contact_form.fill_form')}
                </a>
              </div>
            </motion.div>

            <motion.div
              className="info-card"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.45, delay: 0.16, ease: 'easeOut' }}
            >
              <div className="info-title">
                <FaTelegramPlane />
                {t('contact_form.telegram_bot_title')}
              </div>
              <div className="info-content">
                {t('contact_form.telegram_bot_description')}
                <br /><br />
                <a href="https://t.me/psyhelpinfobot" target="_blank" rel="noopener noreferrer">
                  @psyhelpinfobot
                </a>
              </div>
            </motion.div>
          </div>

          <div className="form-container">
            {submitStatus === 'success' && renderSuccessMessage()}
            {submitStatus === 'partial' && renderPartialSuccessMessage()}
            {submitStatus === 'error' && renderErrorMessage()}
            {(!submitStatus || submitStatus === 'error') && renderForm()}
          </div>
        </div>
      </div>

      <FormThankYouPopup
        open={showThankYou}
        onClose={() => setShowThankYou(false)}
        status={submitStatus === 'partial' ? 'partial' : 'success'}
        title={
          submitStatus === 'partial'
            ? t('contact_form.partial_success_title')
            : t('contact_form.success_title')
        }
        message={
          submitStatus === 'partial'
            ? errorMessage || t('contact_form.partial_success_message')
            : t('contact_form.success_message')
        }
      />
    </section>
  );
};

export default ContactForm; 